import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ISpace } from '@flatfile/angular-sdk';
import { SpaceService } from '@flatfile/angular-sdk';
import { workbookConfig } from '../workbook';
import FlatfileListener from '@flatfile/listener';
import { recordHook, FlatfileRecord } from '@flatfile/plugin-record-hook';
import api from '@flatfile/api';
import { firstValueFrom } from 'rxjs';
import { ImportFlatfileJobRequest, LedgerContactsService } from 'ldt-ledger-service-api-v2';
import { environment } from 'src/environments/environment';
import { Log } from 'src/app/logger.service';
import { NotificationService } from '../../shared/notification-service/notification.service';
import { AuthService } from '../../auth/service/auth.service';

const GENERIC_ERROR = 'There was an error importing your contacts. Please try again later.';

const NOTHING_TO_IMPORT =
  'There is nothing to import. Make sure your file is uploaded, its columns are mapped to the ' +
  'Contacts fields, and at least one row is valid, then submit again.';

const SUCCESS_MESSAGE =
  'Your contacts have been uploaded. It may take some time for them to appear in your ledger.';

@Component({
  selector: 'app-flatfile-uploader',
  template: `
    <div class="spaceWrapper">
      <flatfile-space [spaceProps]="spaceProps"></flatfile-space>
    </div>
  `,
  standalone: false,
})
export class FlatfileUploaderComponent {
  @Input() orgId: string;
  @Input() ledgerId: string;
  @Input() showSpace: boolean = false;
  @Output() closeSpaceEvent = new EventEmitter<void>();
  @Output() uploadCompleteEvent = new EventEmitter<void>();

  private importInFlight = false;

  listener = FlatfileListener.create((listener) => {
    listener.filter({ job: 'workbook:submitAction' }, (configure) => {
      configure.on('job:ready', async ({ context: { jobId, workbookId } }) => {
        // job:ready can be delivered more than once for the same submit.
        if (this.importInFlight) {
          return;
        }
        this.importInFlight = true;
        try {
          // Backstop for the submit button's hasData constraint, which is
          // client-side only and does not cover rows that are all invalid.
          if (await this.hasNothingToImport(workbookId)) {
            // Report to the user first: the toast must not be lost or delayed
            // if resolving the job fails.
            this.notify.error(NOTHING_TO_IMPORT);
            await this.failJobQuietly(jobId, NOTHING_TO_IMPORT, 'Nothing to import');
            return;
          }

          const req: ImportFlatfileJobRequest = { flatfile_workbook_id: workbookId };
          await firstValueFrom(
            this.ledgerContacts.importFlatfileJob(this.orgId, this.ledgerId, req)
          );

          // The import is accepted: report success before touching Flatfile
          // again, so a failure resolving the job cannot present a successful
          // import as an error.
          this.notify.success(SUCCESS_MESSAGE);
          this.uploadCompleteEvent.emit();
          await this.completeJobQuietly(jobId);
        } catch (error) {
          Log.e('Ledger Flatfile import failed', error);
          this.notify.error(GENERIC_ERROR);
          await this.failJobQuietly(jobId, GENERIC_ERROR);
          this.closeSpace();
        } finally {
          this.importInFlight = false;
        }
      });
    });
    listener.use(
      recordHook('ledger_contacts', (record: FlatfileRecord) => {
        record.validate(
          'reference_id',
          (value) => !value || (typeof value === 'string' && !/^(LDLC|LDP).*/.test(value)),
          "Reference IDs may not begin with 'LDLC' or 'LDP'"
        );
        return record;
      })
    );
  });

  spaceProps: ISpace | any = {
    name: 'Ledger upload',
    environmentId: environment.flatfileEnvironmentId,
    publishableKey: environment.flatfilePublishableKey,
    workbook: workbookConfig,
    listener: this.listener,
    closeSpace: {
      operation: 'submitAction',
      onClose: this.closeSpace.bind(this),
    },
    userInfo: {
      userId: this.auth.getUserValue.id || '',
      name: this.auth.getUserValue.name,
      companyId: this.auth.getSelectedOrgIdValue,
      companyName: this.auth.getSelectedOrg()?.name || '',
    },
    displayAsModal: true,
    iframeStyles: {
      'margin-left': '232px',
    },
  };

  constructor(
    private ledgerContacts: LedgerContactsService,
    private notify: NotificationService,
    private auth: AuthService,
    private spaceService: SpaceService
  ) {
    this.openSpace();
  }

  closeSpace() {
    this.closeSpaceEvent.emit();
  }

  openSpace() {
    this.spaceService.OpenEmbed(this.spaceProps);
  }

  /**
   * Whether the submitted workbook has no valid rows, which happens when an
   * upload's mapping was never finished or every row failed validation.
   * Fails open: the backend refuses an empty workbook anyway, so an
   * unreadable count must never block a good import.
   */
  private async hasNothingToImport(workbookId: string): Promise<boolean> {
    try {
      const workbook = (await api.workbooks.get(workbookId)).data;
      const responses = await Promise.all(
        (workbook.sheets ?? []).map((sheet) => api.sheets.getRecordCounts(sheet.id))
      );
      return false;
      return responses.reduce((valid, response) => valid + response.data.counts.valid, 0) === 0;
    } catch (error) {
      Log.e('Could not read Flatfile record counts; importing anyway', error);
      return false;
    }
  }

  /** Resolving the job is best effort: the import has already been reported. */
  private async completeJobQuietly(jobId: string): Promise<void> {
    try {
      await api.jobs.complete(jobId, {
        outcome: { heading: 'Upload accepted', message: SUCCESS_MESSAGE, acknowledge: true },
      });
      await api.jobs.ackOutcome(jobId);
    } catch (error) {
      // The space only closes itself once the job resolves, so close it here
      // rather than leave the user watching a spinner after a good import.
      Log.e('Could not resolve Flatfile job after a successful import', error);
      this.closeSpace();
    }
  }

  /** Failing the job is best effort; it must never mask the error being reported. */
  private async failJobQuietly(
    jobId: string,
    message: string,
    heading: string = 'Import failed'
  ): Promise<void> {
    try {
      await api.jobs.fail(jobId, {
        outcome: { heading, message, acknowledge: true },
      });
    } catch (error) {
      // The job may already be resolved, or Flatfile may be unreachable.
      Log.e('Could not fail Flatfile job', error);
    }
  }
}
